import type { DateTime } from 'luxon';
import { useMemo } from 'react';
import { withoutFullyHiddenEvents } from '@/features/events/lib/eventVisibility.lib';
import {
  type DateTimeRange,
  formatDateTimeRange,
} from '@/features/events/lib/range.lib';
import type { DecoratedExperienceEvent } from '@/features/events/types';
import { usePlannerViewRange } from '@/features/planner/hooks/usePlannerViewRange';
import { prepareAndExpandEvents } from '@/features/printables/groupEvents';
import { usePageExperienceContext } from '../../contexts/PageExperienceContext';

export type ExportableEvent = {
  event: DecoratedExperienceEvent;
  // The first occurrence the view shows, which dates the row. Its end rides
  // along with its start so a multi-day row can show the whole span.
  firstInRange: DateTimeRange;
  isRecurring: boolean;
};

type Result = {
  // One row per series, sorted by first in-range occurrence. Each row's
  // `event` is the original series object (never an expanded copy), so the
  // export stack sends each recurring event once with its rrule intact.
  rows: ExportableEvent[];
  // Carries its own preposition, so the header reads "Events {rangeLabel}".
  rangeLabel: string | null;
};

// The preposition belongs to the label because the shapes don't share one: an
// Upcoming view has a start and no end, and "in September 10, 2026" would read
// as a single day.
const formatScopeLabel = (
  start: DateTime | undefined,
  end: DateTime | undefined
): string | null => {
  const day = (d: DateTime) => d.toFormat('LLLL d, yyyy');

  if (!start && !end) return null;
  if (start && !end) return `from ${day(start)}`;
  if (!start || !end) return `through ${day(end as DateTime)}`;

  const isWholeMonth =
    start.hasSame(end, 'month') &&
    start.hasSame(end, 'year') &&
    start.hasSame(start.startOf('month'), 'day') &&
    end.hasSame(end.endOf('month'), 'day');

  if (isWholeMonth) return `in ${start.toFormat('LLLL yyyy')}`;
  if (start.hasSame(end, 'day')) return `on ${day(start)}`;

  return `in ${formatDateTimeRange({ startDate: start, endDate: end })}`;
};

// Builds the list the sidebar exports: every visible event whose occurrences
// the planner is actually showing, deduped to a single row per series.
// Recurring series that began before the window still appear because we expand
// them and test the occurrences, not the series start.
//
// Scoped to the content window, not the range: the range is the canvas, so a
// view showing Sep 10-20 lays out all of September and scoping by it would offer
// a Sep 22 event that is nowhere on the page.
export const useExportableEvents = (): Result => {
  const { allEvents } = usePageExperienceContext();
  const { range, contentWindow } = usePlannerViewRange();

  // Filtered here rather than taken from the context's `events`: that field is
  // the *view's* set, and under `?hidden=show` it is the unfiltered one. An
  // export is not a view, so what gets sent to a calendar must not depend on
  // whether the page happens to be revealing hidden occurrences.
  const events = useMemo(
    () => withoutFullyHiddenEvents(allEvents),
    [allEvents]
  );

  return useMemo(() => {
    const rangeStart = range?.startDate.startOf('day');
    const rangeEnd = range?.endDate.endOf('day');
    // The content window is the yardstick wherever the view publishes one, pager
    // or not: the Printable List and the month-card sheets have no period to page
    // through but still honor Upcoming and a custom range, so scoping on `range`
    // alone offered events those views never render.
    const scopeStart = contentWindow?.start ?? rangeStart;
    const scopeEnd = contentWindow?.end ?? rangeEnd;
    const rangeLabel = formatScopeLabel(scopeStart, scopeEnd);

    if (!scopeStart && !scopeEnd) {
      // Nothing narrows this view, so everything visible is exportable, one row
      // per series. Sorted like the scoped path below so both agree on order.
      const seen = new Set<string>();
      const rows: ExportableEvent[] = [];
      events.forEach((event) => {
        if (seen.has(event.id)) return;
        seen.add(event.id);
        rows.push({
          event,
          firstInRange: { startDate: event.startDate, endDate: event.endDate },
          isRecurring: !!event.rrule,
        });
      });
      rows.sort(
        (a, b) =>
          a.firstInRange.startDate.toMillis() -
          b.firstInRange.startDate.toMillis()
      );
      return { rows, rangeLabel };
    }

    // A reversed window renders nothing, so nothing is exportable. Explicit
    // because an event spanning both endpoints passes both comparisons below.
    if (scopeStart && scopeEnd && scopeStart > scopeEnd) {
      return { rows: [], rangeLabel };
    }

    // Expand recurrences (same primitive the grid uses) but keep multi-day
    // events whole — we want one row per series, not per day segment. The
    // planner's range goes along so an open-ended series is bounded relative to
    // the window being exported rather than to today; with multi-day expansion
    // off it only sizes that window, and never filters here.
    //
    // `showHiddenEvents` is deliberately not threaded, for the same reason the
    // events above are pre-filtered: a hidden occurrence of a series stays out
    // of the export even while the page is revealing it for editing.
    const occurrences = prepareAndExpandEvents(events, {
      expandRecurrences: true,
      expandMultiDay: false,
      decorateMultiDay: false,
      eventListStartDate: (rangeStart ?? scopeStart)?.toISODate() ?? undefined,
      eventListEndDate: (rangeEnd ?? scopeEnd)?.toISODate() ?? undefined,
    });

    const firstInRangeById = new Map<string, DateTimeRange>();
    occurrences.forEach((occ) => {
      // Mirrors eventOverlapsWindow rather than re-deriving it: an all-day end
      // is exclusive, i.e. midnight after the last day covered, so one landing
      // exactly on scopeStart sits entirely before the window.
      const startsInScope = !scopeEnd || occ.startDate <= scopeEnd;
      const endsInScope =
        !scopeStart ||
        (occ.isAllDay ? occ.endDate > scopeStart : occ.endDate >= scopeStart);
      if (!startsInScope || !endsInScope) return;
      const existing = firstInRangeById.get(occ.id);
      if (!existing || occ.startDate < existing.startDate) {
        firstInRangeById.set(occ.id, {
          startDate: occ.startDate,
          endDate: occ.endDate,
        });
      }
    });

    // Map back to the original series objects, preserving first-seen order for
    // stability before the final sort.
    const rows: ExportableEvent[] = [];
    events.forEach((event) => {
      const firstInRange = firstInRangeById.get(event.id);
      if (!firstInRange) return;
      rows.push({ event, firstInRange, isRecurring: !!event.rrule });
    });

    rows.sort(
      (a, b) =>
        a.firstInRange.startDate.toMillis() -
        b.firstInRange.startDate.toMillis()
    );

    return { rows, rangeLabel };
  }, [events, range, contentWindow]);
};
